import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import { PARTNER_TYPES, accentColorForType } from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Button, IconButton, Switch, Chip, Stack,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, MenuItem, Avatar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/ReceiptLong';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import Tooltip from '@mui/material/Tooltip';
import CircularLoading from '../components/CircularLoading';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';

export default function Partners() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((s) => s.auth);
  const partnerdata = useSelector((s) => s.partnerdata);
  const [filter, setFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');  // ALL | PENDING | APPROVED | REJECTED
  const [search, setSearch] = useState('');

  const { fetchPartners, editPartner, approvePartner } = api;

  useEffect(() => {
    dispatch(fetchPartners());
  }, [dispatch, fetchPartners]);

  const rows = useMemo(() => {
    const list = partnerdata.partners || [];
    return list.filter((p) => {
      if (filter !== 'ALL' && p.partnerType !== filter) return false;
      if (statusFilter !== 'ALL') {
        // PENDING = approved !== true && rejected !== true
        // APPROVED = approved === true
        // REJECTED = approved === false && rejected === true (o approvalNotes presente con flag)
        const isApproved = p.approved === true;
        const isRejected = p.approved === false && !!p.approvalNotes;
        const isPending = !isApproved && !isRejected;
        if (statusFilter === 'PENDING' && !isPending) return false;
        if (statusFilter === 'APPROVED' && !isApproved) return false;
        if (statusFilter === 'REJECTED' && !isRejected) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        return (
          (p.businessName || '').toLowerCase().includes(q) ||
          (p.address?.city || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [partnerdata.partners, filter, statusFilter, search]);

  const onToggleApproved = (partner) => {
    dispatch(approvePartner(partner.id, !partner.approved, auth.profile?.uid));
  };

  const onToggleActive = (partner) => {
    dispatch(editPartner({ ...partner, active: !partner.active }, 'Update'));
  };

  const onDelete = (partner) => {
    if (window.confirm(t('delete_confirm') || 'Delete this partner?')) {
      dispatch(editPartner({ id: partner.id }, 'Delete'));
    }
  };

  if (partnerdata.loading && !partnerdata.partners) return <CircularLoading />;

  return (
    <Box sx={{ p: 2, fontFamily: FONT_FAMILY }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t('admin_partners') || 'Socios'}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ReceiptIcon />}
            onClick={() => navigate('/admin/partners/orders')}
          >
            Pedidos globales
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ backgroundColor: MAIN_COLOR }}
            onClick={() => navigate('/admin/partners/edit/new')}
          >
            {t('add_partner') || 'Nuevo socio'}
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label={t('partner_type') || 'Tipo'}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="ALL">{t('all') || 'Todos'}</MenuItem>
          {Object.values(PARTNER_TYPES).map((pt) => (
            <MenuItem key={pt} value={pt}>{pt}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Estado"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="ALL">Todos</MenuItem>
          <MenuItem value="PENDING">Pendientes</MenuItem>
          <MenuItem value="APPROVED">Aprobados</MenuItem>
          <MenuItem value="REJECTED">Rechazados</MenuItem>
        </TextField>
        <TextField
          size="small"
          placeholder={t('search') || 'Buscar...'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('partner_name') || 'Nombre'}</TableCell>
              <TableCell>{t('partner_type') || 'Tipo'}</TableCell>
              <TableCell>{t('city') || 'Ciudad'}</TableCell>
              <TableCell align="center">{t('approved') || 'Aprobado'}</TableCell>
              <TableCell align="center">{t('active') || 'Activo'}</TableCell>
              <TableCell align="right">{t('actions') || 'Acciones'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar
                      src={p.images?.cover || undefined}
                      variant="rounded"
                      sx={{ width: 40, height: 40 }}
                    >
                      {(p.businessName || '?').slice(0, 1)}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{p.businessName || '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.legalName || ''}</Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    label={p.partnerType}
                    size="small"
                    sx={{
                      backgroundColor: `${accentColorForType(p.partnerType)}1a`,
                      color: accentColorForType(p.partnerType),
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>{p.address?.city || '—'}</TableCell>
                <TableCell align="center">
                  <Switch
                    checked={!!p.approved}
                    onChange={() => onToggleApproved(p)}
                    color="success"
                  />
                  {p.approved ? <CheckCircleIcon sx={{ display: 'none' }} /> : null}
                </TableCell>
                <TableCell align="center">
                  <Switch checked={p.active !== false} onChange={() => onToggleActive(p)} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Pedidos">
                    <IconButton onClick={() => navigate(`/admin/partners/${p.id}/orders`)}>
                      <ReceiptIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Inventario">
                    <IconButton onClick={() => navigate(`/admin/partners/${p.id}/inventory`)}>
                      <InventoryIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="POS · crear pedido">
                    <IconButton onClick={() => navigate(`/admin/partners/${p.id}/pos`)}>
                      <PointOfSaleIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton onClick={() => navigate(`/admin/partners/edit/${p.id}`)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton onClick={() => onDelete(p)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    {t('no_data') || 'Sin datos'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
